/**
 * contract-layout-panel.tsx — Bố cục hợp đồng (tiêu đề, bên bán là Bên A hay Bên B, chế độ toàn văn) + hộp thoại
 * "Nhập từ văn bản" (dán hợp đồng soạn sẵn từ Word → tách Căn cứ / Các bên / Điều / Phụ lục thành điều khoản).
 * Backend: contract-calc.ts (parseLayout) — server kiểm lại; ở đây chỉ gợi ý nhập cho đúng.
 */
import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { parseContractText, type ContractLayout, type ImportedContract, type PartyRole } from './contract-helpers'

export function LayoutPanel({ layout, onChange }: { layout: ContractLayout; onChange: (l: ContractLayout) => void }) {
  const set = <K extends keyof ContractLayout>(k: K, v: ContractLayout[K]) => onChange({ ...layout, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="l-title">Tiêu đề hợp đồng</Label>
          <Input id="l-title" value={layout.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="l-role">Bên bán (ký sẵn) là</Label>
          <select
            id="l-role" value={layout.sellerRole} onChange={(e) => set('sellerRole', e.target.value as PartyRole)}
            className="flex h-8 w-full rounded-md border border-input bg-card px-2 py-1 text-sm"
          >
            <option value="A">Bên A</option>
            <option value="B">Bên B</option>
          </select>
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox checked={layout.partiesInText} onCheckedChange={(v) => set('partiesInText', v === true)} className="mt-0.5" />
        <span>
          <b>Toàn văn</b> — thông tin hai bên đã nằm trong điều khoản (hợp đồng soạn sẵn). Hệ thống sẽ <b>không</b> tự in khối “Hôm nay… hai bên gồm”.
        </span>
      </label>
      <div className="space-y-1 sm:max-w-md">
        <Label htmlFor="l-seller">Tên bên bán hiển thị (tuỳ chọn)</Label>
        <Input
          id="l-seller" value={layout.sellerDisplay} maxLength={120} placeholder={layout.partiesInText ? 'Để trống = không nêu tên' : 'Để trống = tên công ty trong Mẫu'}
          onChange={(e) => set('sellerDisplay', e.target.value)}
        />
        <p className="text-xs text-muted-foreground">Dùng trong email mời ký và trang ký của khách.</p>
      </div>
    </div>
  )
}

export function ImportTextDialog({ onClose, onImport }: { onClose: () => void; onImport: (r: ImportedContract) => void }) {
  const [text, setText] = useState('')
  const [result, setResult] = useState<ImportedContract | null>(null)

  const analyse = () => setResult(parseContractText(text))
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nhập hợp đồng từ văn bản</DialogTitle>
          <DialogDescription>
            Dán toàn bộ nội dung hợp đồng (copy từ Word). Mỗi “Điều N.” và “Phụ lục N.” thành một điều khoản; phần Căn cứ và khối các bên thành
            điều khoản mở đầu. Sẽ <b>thay toàn bộ điều khoản hiện có</b> của hợp đồng này.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="Nội dung hợp đồng" className="min-h-[220px] font-mono text-xs" value={text} placeholder="Dán nội dung hợp đồng vào đây…"
          onChange={(e) => { setText(e.target.value); setResult(null) }}
        />
        {result && (
          <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm" role="status">
            {result.clauses.length > 0
              ? <p><b>{result.clauses.length}</b> điều khoản{result.title ? <> · tiêu đề: <i>{result.title}</i></> : null}</p>
              : <p className="text-destructive">Không tách được điều khoản nào.</p>}
            {result.clauses.length > 0 && (
              <ul className="max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs text-muted-foreground">
                {result.clauses.map((c, i) => <li key={i}>{c.title} <span className="opacity-60">({c.text.length} ký tự)</span></li>)}
              </ul>
            )}
            {result.warnings.map((w, i) => <p key={i} className="text-xs text-warning">⚠ {w}</p>)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button variant="outline" disabled={!text.trim()} onClick={analyse}><FileText className="h-4 w-4" /> Phân tích</Button>
          <Button disabled={!result || result.clauses.length === 0 || result.warnings.length > 0} onClick={() => result && onImport(result)}>Nhập vào hợp đồng</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
